import { useMemo } from "preact/hooks";
import { useAppsCard } from "./store";
import {
  Card, CardContent, Typography, List, ListItem, ListItemAvatar,
  Avatar, ListItemText, IconButton, Collapse, LinearProgress, Box, Tooltip,
  Button, Divider
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AppsIcon from "@mui/icons-material/Apps";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import UpdateIcon from "@mui/icons-material/Update";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 8,
  overflow: "hidden",
  marginBottom: theme.spacing(3),
}));

function fmtDate(d) {
  if (!d) return "—";
  const date = typeof d === "string" || typeof d === "number" ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function averageProgress(tasks) {
  if (!Array.isArray(tasks) || tasks.length === 0) return 0;
  const sum = tasks.reduce((acc, t) => acc + (Number(t?.porcent) || 0), 0);
  return Math.round(sum / tasks.length);
}

export function CardAppsComponent() {
  const {
    apps, expanded, toggleExpand, openAppUrl, role,
    openCreateAppModal, openCreateTaskModal, openEditTaskModal,
  } = useAppsCard();

  const isAdmin = role === 'adevs-admin';

  return (
    <StyledCard elevation={1}>
      <Box
        sx={{
          py: 1.5, px: 2,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          bgcolor: "primary.main",
          color: "primary.contrastText",
        }}
      >
        <Typography variant="h6" color="inherit" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AppsIcon />
            Apps del proyecto
        </Typography>
        {isAdmin && (
          <Button
            size="small"
            onClick={openCreateAppModal}
            startIcon={<AddIcon />}
            sx={{
              borderColor: t => t.palette.primary.contrastText,
              color: t => t.palette.primary.contrastText,
              borderWidth: 1,
              borderStyle: "solid",
              "&:hover": { bgcolor: "rgba(255,255,255,.12)", borderColor: t => t.palette.primary.contrastText },
            }}
          >
            Nueva App
          </Button>
        )}
      </Box>

      <CardContent>
        {!apps?.length ? (
          <Typography variant="body2" color="text.secondary">Aún no hay apps registradas.</Typography>
        ) : (
          <List disablePadding>
            {apps.map((app) => {
              const isOpen = !!expanded[app._id];
              const progress = averageProgress(app.tasks);

              return (
                <Box key={app._id} sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2, mb: 1, overflow: "hidden" }}>
                  <ListItem
                    secondaryAction={
                      <Box sx={{ display: "flex", alignItems: "center", gap: .5 }}>
                        <Tooltip title={app.appUrl ? "Abrir app" : "Sin URL"}>
                          <span>
                            <IconButton size="small" onClick={() => openAppUrl(app.appUrl)} disabled={!app.appUrl}>
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <IconButton size="small" onClick={() => toggleExpand(app._id)}>
                          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: "primary.main" }}><AppsIcon /></Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{app.name}</Typography>
                          <Typography variant="body2" color="text.secondary">{progress}% listo</Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: .5 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: .75 }}>{app.description || "Sin descripción"}</Typography>
                          <LinearProgress variant="determinate" value={progress} />
                        </Box>
                      }
                    />
                  </ListItem>
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Divider />
                    <Box sx={{ px: 2, pb: 2, pt: 1.5 }}>
                      {isAdmin && (
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                          <Button size="small" startIcon={<AddIcon />} onClick={() => openCreateTaskModal(app._id)}>
                            Nueva tarea
                          </Button>
                        </Box>
                      )}
                      {!app.tasks?.length ? (
                        <Typography variant="body2" color="text.secondary">No hay tareas.</Typography>
                      ) : (
                        app.tasks.map((t) => (
                          <Box key={t._id} sx={{ mb: 1.75 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: .5, gap: 2 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.name}</Typography>
                              <Box sx={{ display: "inline-flex", alignItems: "center", gap: .5 }}>
                                <Typography variant="caption" color="text.secondary">{t.porcent ?? 0}%</Typography>
                                {isAdmin && (
                                  <Tooltip title="Editar porcentaje">
                                    <IconButton size="small" onClick={() => openEditTaskModal(t)}>
                                      <EditIcon fontSize="inherit" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                            <LinearProgress variant="determinate" value={Number(t.porcent ?? 0)} />
                            {t.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: .5 }}>
                                {t.description}
                              </Typography>
                            )}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: .5 }}>
                              <Box sx={{ display: "inline-flex", alignItems: "center", gap: .5 }}>
                                <AccessTimeIcon fontSize="inherit" />
                                <Typography variant="caption" color="text.secondary">
                                  Creado: {fmtDate(t.created_at || t.createdAt)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: "inline-flex", alignItems: "center", gap: .5 }}>
                                <UpdateIcon fontSize="inherit" />
                                <Typography variant="caption" color="text.secondary">
                                  Últ. cambio: {fmtDate(t.updated_at || t.updatedAt)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ))
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </List>
        )}
      </CardContent>
    </StyledCard>
  );
}